import { useCallback, useMemo, useRef } from 'react';
import { mergeRefs } from '@/utils/refs';
import type { Ref } from 'react';

type UseAppContextMenuBehaviorArgs = {
  noClose: boolean;
  onClose: (e?: React.MouseEvent) => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
  ref: Ref<HTMLDivElement>;
};

function getAdjacentMenuItemIndex(currentIndex: number, direction: 'next' | 'previous', itemCount: number): number {
  if (direction === 'next') {
    return currentIndex + 1 >= itemCount ? 0 : currentIndex + 1;
  }

  return currentIndex - 1 < 0 ? itemCount - 1 : currentIndex - 1;
}

export function useAppContextMenuBehavior({ noClose, onClose, onKeyDown, ref }: UseAppContextMenuBehaviorArgs) {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const mergedMenuRef = useMemo(() => mergeRefs(menuRef, ref), [ref]);

  const blurFocusedMenuDescendant = useCallback(() => {
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement && menuRef.current?.contains(activeElement)) {
      activeElement.blur();
    }
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.stopPropagation();

        const activeElement = document.activeElement;
        const menuElement = e.currentTarget;
        const menuItems = Array.from(menuElement.querySelectorAll('li'));

        if (menuItems.length === 0) return;

        const isMenuItemFocused = menuItems.some((item) => item === activeElement);

        if (!isMenuItemFocused) {
          const hoveredItem = menuItems.find((item) => item.matches(':hover'));
          let targetIndex: number;
          if (hoveredItem) {
            targetIndex = getAdjacentMenuItemIndex(
              menuItems.indexOf(hoveredItem),
              e.key === 'ArrowDown' ? 'next' : 'previous',
              menuItems.length,
            );
          } else {
            targetIndex = e.key === 'ArrowDown' ? 0 : menuItems.length - 1;
          }

          const targetItem = menuItems[targetIndex];
          if (targetItem instanceof HTMLElement) {
            targetItem.focus();
          }
        }
      }
      onKeyDown?.(e);
    },
    [onKeyDown],
  );

  const handleClose = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      blurFocusedMenuDescendant();
      onClose(e);
    },
    [blurFocusedMenuDescendant, onClose],
  );

  const handleCloseActions = useCallback(
    (e?: React.MouseEvent<HTMLElement>) => {
      if (noClose) return;
      blurFocusedMenuDescendant();
      onClose(e);
    },
    [blurFocusedMenuDescendant, noClose, onClose],
  );

  return {
    handleClose,
    handleCloseActions,
    handleKeyDown,
    mergedMenuRef,
  };
}
